import { Routes } from '@angular/router';
import { KfSessionGuard, matchUnitType } from 'shared';
import { TANNERY_MOUNT, TANNERY_UNIT_TYPE } from 'tannery/core';
import { ACCESSORIES_MOUNT, ACCESSORIES_UNIT_TYPE } from 'accessories/core';

/**
 * The route objects that mount each vertical, kept apart from the routing module so a spec can
 * run the real ones: the routing module pulls in the whole base app, this file only `shared` and
 * the verticals' `core` entry points. The screens arrive through `import(...)` inside
 * `loadChildren`, never as a static import.
 */

/**
 * The tannery vertical, from its library, namespaced under one segment so a single guard
 * covers all of it and none of its paths can collide with the flat ones above.
 * canMatch, not canActivate: canActivate runs after the router has downloaded the lazy
 * chunk, so a rexine tenant would fetch the tannery bundle and only then be bounced
 * (ADR-P-001 §5.1). A failed canMatch falls through, so the same path redirects —
 * absolutely, so a deep link's leftover segments are dropped rather than appended — to the
 * denied page and names the area it was for. Only tannery/core is imported eagerly here;
 * the screens arrive through loadChildren.
 */
export const TANNERY_MOUNT_ROUTES: Routes = [
  {
    path: TANNERY_MOUNT,
    canMatch: [matchUnitType(TANNERY_UNIT_TYPE)],
    canActivate: [KfSessionGuard],
    loadChildren: () => import('tannery').then(m => m.TANNERY_ROUTES),
  },
  { path: TANNERY_MOUNT, redirectTo: `/home/denied/${TANNERY_UNIT_TYPE}` },
];

/**
 * The accessories vertical: the same shape as the tannery block above, and for the same
 * reasons (canMatch before the chunk is fetched, an absolute same-path fallback to the
 * denied page; accessories integration spec §2.3). Only accessories/core is imported here.
 */
export const ACCESSORIES_MOUNT_ROUTES: Routes = [
  {
    path: ACCESSORIES_MOUNT,
    canMatch: [matchUnitType(ACCESSORIES_UNIT_TYPE)],
    canActivate: [KfSessionGuard],
    loadChildren: () => import('accessories').then(m => m.ACCESSORIES_ROUTES),
  },
  { path: ACCESSORIES_MOUNT, redirectTo: `/home/denied/${ACCESSORIES_UNIT_TYPE}` },
];

/** The verticals' print pages, mounted at the top level, outside the shell layout. */
export const VERTICAL_PRINT_ROUTES: Routes = [
  {
    // loadChildren, not `component:`. TANNERY_PRINT_ROUTES lives in the tannery
    // library's primary entry point, so naming its component here would be a
    // static import of the whole tannery bundle from an eagerly-evaluated file —
    // every spec-sheet screen would land in the initial graph for a route only
    // the print button opens. canMatch, not canActivate: the print pages read
    // identity from persisted storage, matching poprint and vesseltagprint above.
    path: 'tannery/print',
    canMatch: [matchUnitType(TANNERY_UNIT_TYPE)],
    loadChildren: () => import('tannery').then(m => m.TANNERY_PRINT_ROUTES),
  },
  {
    // As tannery/print above: loadChildren keeps the print pages out of the initial graph
    // (accessories integration spec §2.4).
    path: `${ACCESSORIES_MOUNT}/print`,
    canMatch: [matchUnitType(ACCESSORIES_UNIT_TYPE)],
    loadChildren: () => import('accessories').then(m => m.ACCESSORIES_PRINT_ROUTES),
  },
];
